import { Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, Output, QueryList, SimpleChanges, ViewChild, ViewChildren, ChangeDetectionStrategy, ChangeDetectorRef, TemplateRef, inject } from "@angular/core";
import { ExpansionPanelComponent, IPopoverOptions, PopoverConfig, PopoverRef, PopoverService } from "@tylertech/forge-angular";
import { TreeNode } from "@tylertech/tyler-api-types";
import { ContextMenuData, ContextMenuItem, MouseButton } from "../../interfaces";
import { ExpandableTreeNode, NavigationService, NavMenuService, SecurityService, ToastService } from "../../services";
import { ContextMenuComponent } from "./contextmenu.component";
import { ListComponent } from "@tylertech/forge";
import { CopyToClipboardToken } from "../../providerTokens";
import { Subscription } from "rxjs";

const ADD_FAVORITE = "addFavorite";

@Component({
    selector: "tree",
    templateUrl: "./tree.component.html",
    styleUrl: "./tree.component.scss",
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class TreeComponent implements OnChanges, OnDestroy
{
    private readonly changeDetectorRef = inject(ChangeDetectorRef);
    private readonly forgePopoverService = inject(PopoverService);
    private readonly $navigationService = inject(NavigationService);
    private readonly $navMenuService = inject(NavMenuService);
    private readonly $toastService = inject(ToastService);
    private readonly copyToClipboardFn = inject(CopyToClipboardToken);
    private readonly $securityService = inject(SecurityService);

    @Input() dataSource: TreeNode;
    @Input() selected: Readonly<TreeNode>;
    @Input() filter?: string;
    @Input() depth: number = 0;

    @Output() protected readonly select = new EventEmitter<TreeNode>();

    @ViewChild("searchTree") protected readonly searchTree: ElementRef<ListComponent>;
    @ViewChildren("nodeItem") protected readonly nodeItem: QueryList<ElementRef>;
    @ViewChildren("topMenuItem") topMenuItems: QueryList<ExpansionPanelComponent>;

    children: Array<TreeNode> = [];

    protected expanding: boolean = false;
    protected collapsing: boolean = false;
    protected popoverRef: PopoverRef<ContextMenuComponent>;

    private initialExpandedNodeSet = false;
    protected popoverSubscription: Subscription;

    ngOnChanges(changes: SimpleChanges): void
    {
        if (changes.dataSource)
            this.children = this.mapPathlessForms() ?? [];

        if (this.depth !== 0)
            return;

        if (changes.filter && changes.filter.previousValue !== changes.filter.currentValue && this.searchTree)
            this.searchTree.nativeElement.scrollTop = 0;
        requestAnimationFrame(()=>
        {
            if (changes.selected && changes.selected.currentValue && this.topMenuItems?.toArray().length > 0)
            {
                const selected: TreeNode = changes.selected.currentValue;
                this.dataSource.Children.forEach((c: ExpandableTreeNode) =>
                {
                    if (!this.$navMenuService.hasSelectedChild(c, selected))
                    {
                        const item = this.topMenuItems.toArray().find(x => x.nativeElement.attributes.getNamedItem("tree-path")?.value === c.TreePath);
                        if (item)
                        {
                            this.collapseNode(c);
                        }
                    }
                    else
                    {
                        this.closeSiblingsOfSelectedNode(c, selected);
                    }

                    this.scrollToActiveNode();
                });
            }
        });
    }

    protected scrollToActiveNode(redirectFocus: boolean = true): void
    {
        if (this.searchTree)
        {
            const selectedEl = this.searchTree.nativeElement.querySelector("forge-list-item[selected]");
            selectedEl?.scrollIntoView(false);

            if (redirectFocus)
            {
                document.documentElement.tabIndex = 0;
                document.documentElement.focus();
                document.documentElement.removeAttribute("tabindex");
            }
        }
    }

    protected closeSiblingsOfSelectedNode(node: ExpandableTreeNode, selected: ExpandableTreeNode): void
    {
        if (node.Children)
        {
            node.Children.forEach(c =>
            {
                this.closeSiblingsOfSelectedNode(c, selected);

                if (!this.$navMenuService.hasSelectedChild(c, selected))
                {
                    this.collapseNode(c);
                }
            });
        }
    }

    mapPathlessForms(): Array<TreeNode>
    {
        const children: Array<TreeNode> = [];
        this.dataSource?.Children.forEach(c =>
        {
            if (c.Key === "")
                c.Children.forEach(cc =>
                {
                    this.setShowTooltip(cc);
                    children.push(cc);
                });
            else
            {
                this.setShowTooltip(c);
                children.push(c);
            }
        });

        return children.sort((a, b) =>
        {
            if (a.Key === "My Favorites")
                return -1;
            if (b.Key === "My Favorites")
                return 1;
            return 0;
        });
    }

    get isProvisional(): boolean
    {
        return this.$securityService.isProvisional;
    }

    get isFirstLevel(): boolean
    {
        return this.depth === 0;
    }

    get listStyles(): Record<string, string>
    {
        if (this.depth > 0)
        {
            return {
                /* eslint-disable @typescript-eslint/naming-convention */
                "margin-left": "1rem"
                /* eslint-enable @typescript-eslint/naming-convention */
            };
        }
    }

    isSelected(node: TreeNode): boolean
    {
        return this.selected
            && this.selected.Execute === node.Execute
            && this.selected.TreePath === node.TreePath;
    }

    onTreeClick(node: ExpandableTreeNode): void
    {
        if (!node.$expanded || this.collapsing)
        {
            if (this.collapsing)
            {
                this.expanding = true;
            }
            node.$expanded = true;
        }
        else
        {
            this.collapsing = true;
            this.expanding = false;
            // need to delay hiding for the animation UX and set properties to not collide with forge expansion panel toggling
            setTimeout(
                () =>
                {
                    if (!this.expanding)
                    {
                        this.collapseNode(node);
                    }
                    this.expanding = false;
                    this.collapsing = false;
                    this.changeDetectorRef.markForCheck();
                },
                300);
        }
    }

    protected collapseNode(node: ExpandableTreeNode): void
    {
        node.$expanded = false;
        document.querySelectorAll(`forge-expansion-panel[tree-path="${node.TreePath}"]`).forEach(panel => {
            panel.removeAttribute("open");
        });

        if (node.Children)
        {
            node.Children.forEach(c =>
            {
                this.collapseNode(c);
            });
        }
    }

    isTreeNodeExpanded(node: ExpandableTreeNode): boolean
    {
        return node.$expanded;
    }

    isPanelExpandedInitial(node: ExpandableTreeNode): boolean
    {
        if (this.filter?.length > 0)
        {
            node.$expanded = true;
            return true;
        }

        if (this.selected)
        {
            const expanded = this.selected.TreePath.startsWith(node.TreePath);
            if (expanded && !this.initialExpandedNodeSet)
            {
                node.$expanded = true;
                this.initialExpandedNodeSet = true;
            }

            return expanded;
        }

        return false;
    }

    setShowTooltip(node: TreeNode): void
    {
        let limit = 41;
        if (node.Children?.length > 0)
        {
            limit -= 8;
        }

        if (node.TreePath)
        {
            const nested = (node.TreePath.match(/\//g) || []).length;
            limit -= (nested * 5);
        }

        node["$showTooltip"] = node.Key.length > limit;

        node.Children?.forEach(child => this.setShowTooltip(child));
    }

    showTooltip(node: TreeNode): boolean
    {
        return node["$showTooltip"] ?? false;
    }

    onLeafClicked($event: Event, node: TreeNode): void
    {
        let closeMenu = false;

        if (($event as MouseEvent)?.ctrlKey)
        {
            $event.stopPropagation();
            this.openInNewTab(node);
            closeMenu = true;
        }

        if(($event as KeyboardEvent)?.key === "Enter"
            || ($event as KeyboardEvent)?.key === " "
            || (($event as MouseEvent)?.button === MouseButton.Main && ($event as MouseEvent)?.ctrlKey !== true))
        {
            this.dataSource.Children.forEach(c => this.deselectAllNodes(c));
            node.Selected = true;
            this.$navigationService.navigateToUrl(node.Execute);
            closeMenu = true;
        }

        if (!this.$navMenuService.IsNavPinned && closeMenu)
        {
            this.$navMenuService.toggle();
        }
    }

    deselectAllNodes(node: TreeNode): void
    {
        if (node.Children)
            node.Children.forEach(c => this.deselectAllNodes(c));
        else
            node.Selected = false;
    }

    // TODO - inline
    protected closePopovers(): void
    {
        this.popoverSubscription?.unsubscribe();
        this.popoverSubscription = undefined;
        this.popoverRef?.close();
        this.popoverRef = undefined;
    }

    ngOnDestroy(): void
    {
        this.closePopovers();
    }

    onLeafContextMenu($event: Event, node: TreeNode): void
    {
        $event.preventDefault();

        this.closePopovers();

        const anchorElement = $event.target as HTMLElement;
        const popupOptions: IPopoverOptions = {
            anchorElement,
            placement: "bottom"
        };

        const favoriteMenuItem = this.buildFavoriteMenuItem(node);
        const copyMenuItem = this.buildCopyMenuItem(node);
        const newTabMenuItem = this.buildNewTabMenuItem(node);
        const popupConfig: PopoverConfig<ContextMenuData> = {
            data: {
                items: [
                    newTabMenuItem,
                    copyMenuItem,
                    favoriteMenuItem
                ]
            }
        };

        this.popoverRef = this.forgePopoverService.show(ContextMenuComponent, popupOptions, popupConfig);
        this.popoverSubscription = this.popoverRef.afterClosed
            .subscribe(() =>
            {
                this.popoverRef = undefined;
                this.popoverSubscription = undefined;
            });
    }

    protected buildFavoriteMenuItem(node: TreeNode): ContextMenuItem
    {
        return {
            value: ADD_FAVORITE,
            label: this.isFavoriteListItem(node)
                ? "Remove from favorites"
                : "Add to favorites",
            onClick: () =>
            {
                this.closePopovers();
                this.$toastService.showToast("Updating My Favorites...", 2750);

                if (this.isFavoriteListItem(node))
                {
                    this.$navMenuService.removeFromFavorites(node);
                    return;
                }

                this.$navMenuService.addToFavorites(node);
            },
        };
    }

    protected buildCopyMenuItem(node: TreeNode): ContextMenuItem
    {
        return {
            value: "copyPath",
            label: "Copy path",
            onClick: () =>
            {
                this.closePopovers();
                this.copyPathToClipboard(node);
            },
        };
    }

    protected buildNewTabMenuItem(node: TreeNode): ContextMenuItem
    {
        return {
            value: "newTab",
            label: "Open in new tab",
            onClick: () =>
            {
                this.closePopovers();
                this.openInNewTab(node);
            }
        };
    }

    private isFavoriteListItem(node: TreeNode): boolean
    {
        return node.TreePath.split("/")[0] === "My Favorites";
    }

    protected openInNewTab(node: TreeNode): void
    {
        const path = node.Execute.replace("//", "/");
        const parts = path.split("/");
        const start = parts.indexOf("apps") + 1;
        const route = parts.slice(start, parts.length).join("/");
        const url = this.$navigationService.getLinkToApp(route) as string;
        window.open(url, "_blank");
        window.focus();
    }

    protected copyPathToClipboard(node: TreeNode): void
    {
        const prettyPath = node.TreePath.replace(/\//g, " > ");
        this.copyToClipboardFn(prettyPath);
    }

    onLeafMouseup($event: MouseEvent, node: TreeNode): void
    {
        if ($event.button === MouseButton.Auxiliary)
        {
            $event.stopPropagation();
            this.openInNewTab(node);
        }
    }

    onLeafMouseDown($event: MouseEvent): void
    {
        if ($event.button === MouseButton.Auxiliary)
        {
            $event.preventDefault();
        }
    }

    getListItemClasses(node: TreeNode): Record<string, boolean>
    {
        /* eslint-disable @typescript-eslint/naming-convention */
        return {
            "search-tree--item-root": !node.TreePath?.includes("/")
        };
        /* eslint-enable @typescript-eslint/naming-convention */
    }

    getLeafNodeClasses(node: TreeNode): Record<string, boolean>
    {

        return {
            "highlighted": this.isHighlighted(node)
        };

    }

    private isHighlighted(node: TreeNode): boolean
    {
        if (this.filter)
        {
            if (node.Children)
            {
                return false;
            }

            return node.Key.toLowerCase()
                .includes(this.filter.toLowerCase());
        }
    }

    getAccessibleId(node: TreeNode, displayedIndex: number): string
    {
        const path = node.TreePath?.replace(/[ /]/g, "");
        return `${path}${displayedIndex}item`;
    }


    onSelect($event: TreeNode): void
    {
        this.select.emit($event);
    }

    trackByTreePath(index: number, node: TreeNode): string
    {
        return node.TreePath;
    }
}
